import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { IContribution } from '@nxsys/types/models/aepn/contribution';
import { IPayroll } from '@nxsys/types/models/payroll';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { ColumnsType } from 'antd/es/table';
import { find } from 'lodash';

export type ContributionsColumnsParams = {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isErrorTab?: boolean;
  isBureau?: boolean;
};

const getStatusText = (status: string) => {
	const statusConfig: Record<string, { color: string; label: string }> = {
		PENDING: { color: '#f0ad4e', label: 'Pending' },
		WAITING_UPLOAD: { color: '#f0ad4e', label: 'Waiting Upload File' },
		IN_PROGRESS: { color: '#17a2b8', label: 'In-Progress' },
		SUCCESS: { color: '#28a745', label: 'Success' },
		SUCCESS_WITH_WARNINGS: { color: '#ffc107', label: 'Success with warnings' },
		ERROR: { color: '#dc3545', label: 'Error' },
		NO_DATA_FOUND: { color: '#6c757d', label: 'No Data Found' },
	};
	const config = statusConfig[status] || { color: '#333', label: status || '_' };
	return (
		<span style={{ color: config.color, fontWeight: 600 }}>
			{config.label}
		</span>
	);
};

export const getContributionsSubmittedColumns = ({
  messages,
  payrollCountry,
  isErrorTab,
  isBureau,
}: ContributionsColumnsParams): ColumnsType<IContribution> => {
  const currency = showCurrencyByCountry(payrollCountry);
  return [
    ...(isErrorTab
      ? ([
          {
            title: messages['table.error'],
            dataIndex: 'errorDescription',
            width: 350,
            render: (value: string) => (
              <span style={{ color: '#dc3545', fontWeight: 600 }}>
                {value || '_'}
              </span>
            ),
          },
        ] as ColumnsType<IContribution>)
      : []),
    {
      title: messages['common.status'],
      dataIndex: 'status',
      align: 'center',
      width: 150,
      render: (value: string) => (value ? getStatusText(value) : '_'),
    },
    {
      title: messages['common.taxPeriod'],
      align: 'right',
      width: 150,
      render: (record: IContribution) =>
        `${record?.taxPeriod || ''}/${record?.taxYear || ''}`,
    },
    {
      title: messages['common.employerRef'],
      dataIndex: 'subcontractor',
      width: 150,
      render: (subcontractor) => subcontractor?.agencyRef || '_',
    },
    {
      title: messages['table.employeeName'],
      dataIndex: 'subcontractor',
      width: 200,
      render: (subcontractor) =>
        subcontractor?.fullName ||
        `${subcontractor?.forename || ''}${subcontractor?.surname || ''}` ||
        '_',
    },
    {
      title: isBureau
        ? messages['table.employer']
        : messages['table.paymentCompany'],
      dataIndex: 'submission',
      width: 200,
      render: (submission) => submission?.company?.companyName || '_',
    },
    {
      title: messages['table.grossForPension'],
      dataIndex: 'grossPay',
      align: 'right',
      width: 180,
      render: (value: number) => formatMoney(value, currency),
    },
    {
      title: messages['table.employeeContribution'],
      dataIndex: 'eeContribution',
      align: 'right',
      width: 200,
      render: (value: number) => formatMoney(value, currency),
    },
    {
      title: messages['table.employerContribution'],
      dataIndex: 'erContribution',
      align: 'right',
      width: 200,
      render: (value: number) => formatMoney(value, currency),
    },
    {
      title: messages['table.totalContribution'],
      align: 'right',
      width: 180,
      render: (record: IContribution) => {
        const total =
          Number(record?.eeContribution || 0) +
          Number(record?.erContribution || 0);
        return formatMoney(total, currency);
      },
    },
  ];
};

export type PendingColumnsParams = {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  isBureau?: boolean;
};

export const getContributionsPendingColumns = ({
  messages,
  payrollCountry,
  isBureau,
}: PendingColumnsParams): ColumnsType<IPayroll> => {
  const currency = showCurrencyByCountry(payrollCountry);
  return [
    {
      title: messages['common.employerRef'],
      dataIndex: 'subcontractorRef',
      width: 150,
      render: (subcontractorRef) => subcontractorRef?.agencyRef || '_',
    },
    {
      title: messages['table.employeeName'],
      dataIndex: 'subcontractorRef',
      width: 200,
      render: (subcontractor) =>
        subcontractor?.fullName ||
        `${subcontractor?.forename || ''}${subcontractor?.surname || ''}` ||
        '_',
    },
    {
      title: messages['common.taxPeriod'],
      align: 'right',
      width: 150,
      render: (record: IPayroll) =>
        `${record?.taxPeriod || ''}/${record?.taxYear || ''}`,
    },
    {
      title: messages['common.payFrequency'],
      dataIndex: 'payFrequency',
      align: 'center',
      width: 160,
      render: (value: string) =>
        find(defaultPaymentFrequency, (item) => item?.value === value)?.label ||
        '_',
    },
    {
      title: isBureau
        ? messages['table.employer']
        : messages['table.paymentCompany'],
      dataIndex: 'companyRef',
      width: 200,
      render: (companyRef) => companyRef?.companyName || '_',
    },
    {
      title: messages['common.grossPay'],
      dataIndex: 'gross',
      align: 'right',
      width: 180,
      render: (value: number) => formatMoney(value, currency),
    },
    {
      title: messages['common.pensionableGross'],
      dataIndex: 'irelandValue',
      align: 'right',
      width: 180,
      render: (irelandValue) => formatMoney(irelandValue?.pensionGross, currency),
    },
    {
      title: messages['table.employeeContribution'],
      dataIndex: 'irelandValue',
      align: 'right',
      width: 200,
      render: (irelandValue) =>
        formatMoney(irelandValue?.eePension, currency),
    },
    {
      title: messages['table.employerContribution'],
      dataIndex: 'irelandValue',
      align: 'right',
      width: 200,
      render: (irelandValue) => formatMoney(irelandValue?.erPension, currency),
    },
  ];
};
